import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Area } from '../types';
import { near } from '../area-match';
import { useAnchoredPopover } from './useAnchoredPopover';

/**
 * THE AREA FIELD on a card — a type-to-create combobox, the twin of the Epic
 * field's type-to-create input. It replaces the old closed <select>, which was
 * the whole reason people filed a standing domain under an Epic instead: the Epic
 * field let you type a new one on the spot, the Area field made you leave for
 * Settings first. Now an area is made right here.
 *
 * Type to filter the areas that exist; pick one, clear to "No area", or create a
 * new one in place ("+ Create "<name>""), which calls store.createArea and selects
 * it at once. A near-match nudge ("Did you mean Network?") offers the close
 * existing area before a near-duplicate is made — but never blocks it, so the
 * field stays flexible (you can always create what you typed). Closes on Escape,
 * a pick, or a click away, like every other popover in the app.
 */

interface Props {
  /** The selected area's id, or '' for "No area". */
  value: string;
  /** The areas on offer — live ones, plus this card's own if it was retired. */
  areas: Area[];
  disabled: boolean;
  onChange: (id: string) => void;
  /** Make a new area with this name; resolves to its id, or an error to show. */
  onCreate: (name: string) => Promise<{ id: string } | { error: string }>;
  /** Open on mount — the Grid's in-cell editor; omit on the card. */
  autoOpen?: boolean;
  /** Fired when the popover closes, with how (the Grid moves the cell on it). */
  onClose?: (reason: 'pick' | 'cancel' | 'away' | 'next' | 'prev') => void;
}

// The check on the currently-selected option, so reopening the picker shows which
// area is set — the same mark the colour picker uses (auto-aligned right, brand
// colour, via `.sr-ta-opt > svg:last-child`).
function AreaTick() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AreaField({ value, areas, disabled, onChange, onCreate, autoOpen = false, onClose }: Props) {
  const [open, setOpen] = useState(autoOpen);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const reason = useRef<'pick' | 'cancel' | 'away' | 'next' | 'prev'>('away');
  const wasOpen = useRef(open);
  // Fire onClose once per open→closed transition, however it closed.
  useEffect(() => {
    if (wasOpen.current && !open) onClose?.(reason.current);
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const pos = useAnchoredPopover(open, wrap, pop);
  // The popover is rendered visibility:hidden until useAnchoredPopover has
  // measured a position, and focusing a hidden element is silently dropped — so
  // gating on `open` alone left the search box unfocused and type-to-find dead
  // until you clicked it (interaction witness, 17 Oct). This boolean flips true
  // the moment the box becomes focusable, and — being a plain boolean, not the
  // pos object — does not re-fire as the popover re-places on scroll or as the
  // list height changes while you type.
  const visible = open && !!pos;

  const selected = areas.find((a) => a.id === value);

  useEffect(() => {
    if (visible) input.current?.focus({ preventScroll: true });
  }, [visible]);

  // Close on a click away, or on Escape, like every menu/popover in the app. The
  // popover is portaled outside the field, so let a click inside it (or the field)
  // through. Escape is caught in the document CAPTURE phase and stopped there:
  // when this field is on a card, the card closes on Escape through a window-level
  // (bubble) listener, so the menu's own React handler never beats it and the
  // whole card would drop out of edit instead of the menu closing. Capturing it
  // first gives the open menu first refusal, whatever holds focus (see ColourField).
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrap.current?.contains(t) || pop.current?.contains(t)) return;
      reason.current = 'away';
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        reason.current = 'cancel';
        setOpen(false);
      }
    };
    // Close when focus leaves the control (e.g. Tab-away) so the menu never
    // lingers open out of focus and swallows the next Escape (interaction witness,
    // 17 Oct). Deferred so a WebKit option click — which does not move focus —
    // is not read as leaving.
    const onFocusOut = () => {
      setTimeout(() => {
        const el = document.activeElement;
        // Focus still settling into the just-opened portaled menu (parked on
        // <body> for a tick before the visible-effect moves it in) — not a leave.
        // Closing here would kill the editor before its first key (interaction
        // witness, 19 Oct). A real Tab-away lands on an element, never <body>.
        if (!el || el === document.body) return;
        if (wrap.current?.contains(el) || pop.current?.contains(el)) return;
        reason.current = 'away';
        setOpen(false);
      }, 0);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  const close = (why: 'pick' | 'cancel' | 'next' | 'prev' = 'pick') => {
    reason.current = why;
    setOpen(false);
    setQ('');
    setErr('');
  };
  const pick = (id: string, why: 'pick' | 'next' | 'prev' = 'pick') => {
    onChange(id);
    close(why);
  };

  const needle = q.trim().toLowerCase();
  const matches = areas
    .filter((a) => !needle || a.name.toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 8);
  // A create is offered whenever the typed name is not already a live area.
  const exactLive = areas.some((a) => !a.retiredAt && a.name.toLowerCase().trim() === needle);
  const canCreate = needle.length > 0 && !exactLive;
  const nearArea = canCreate ? areas.find((a) => !a.retiredAt && near(a.name, q)) : undefined;

  const doCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    setErr('');
    const res = await onCreate(q.trim());
    setCreating(false);
    if ('id' in res) pick(res.id);
    else setErr(res.error);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Swallow it so it closes only the popover, the way a native <select>
      // does — without bubbling up to the card and dropping it out of edit mode.
      e.preventDefault();
      e.stopPropagation();
      close('cancel');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (matches.length) pick(matches[0].id);
      else if (canCreate) void doCreate();
    } else if (e.key === 'Tab' && onClose) {
      // In the grid, Tab steps to the next cell (Shift+Tab the previous). If the
      // box holds a query that matches, commit the top match like Enter; otherwise
      // leave the value as it is and just move. preventDefault keeps focus from
      // leaving before the grid re-places it. On the card (no onClose) Tab is left
      // to the browser, as it was.
      e.preventDefault();
      e.stopPropagation();
      const dir = e.shiftKey ? 'prev' : 'next';
      if (needle && matches.length) pick(matches[0].id, dir);
      else close(dir);
    }
  };

  return (
    <span className="area-combo" ref={wrap}>
      <button
        type="button"
        className="ctl quiet area-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Area"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {selected ? (
          <>
            <span className="epic-sw area-trigger-sw" style={{ background: selected.colour || 'var(--t3)' }} aria-hidden="true" />
            <span className="area-trigger-name">
              {selected.name}
              {selected.retiredAt ? ' (retired)' : ''}
            </span>
          </>
        ) : (
          <span className="area-trigger-name area-trigger-none">No area</span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <input
            ref={input}
            className="ctl sr-ta-input"
            placeholder="Find or create an area…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setErr('');
            }}
            onKeyDown={onKeyDown}
          />
          <div className="sr-ta-body">
            {nearArea && (
              <button type="button" className="sr-ta-opt area-near" onClick={() => pick(nearArea.id)}>
                <span className="epic-sw" style={{ background: nearArea.colour || 'var(--t3)' }} aria-hidden="true" />
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">Did you mean {nearArea.name}?</span>
                  <span className="sr-ta-sub">pick the area you already have</span>
                </span>
              </button>
            )}

            <button type="button" className="sr-ta-opt" aria-selected={value === ''} onClick={() => pick('')}>
              <span className="epic-sw epics-sw-none" aria-hidden="true" />
              <span className="sr-ta-opt-text"><span className="sr-ta-name">No area</span></span>
              {value === '' && <AreaTick />}
            </button>

            {matches.map((a) => (
              <button type="button" className="sr-ta-opt" key={a.id} aria-selected={a.id === value} onClick={() => pick(a.id)}>
                <span className="epic-sw" style={{ background: a.colour || 'var(--t3)' }} aria-hidden="true" />
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">
                    {a.name}
                    {a.retiredAt ? ' (retired)' : ''}
                  </span>
                </span>
                {a.id === value && <AreaTick />}
              </button>
            ))}

            {canCreate && (
              <button type="button" className="sr-ta-opt area-create" onClick={() => void doCreate()} disabled={creating}>
                <span className="area-create-plus" aria-hidden="true">+</span>
                <span className="sr-ta-opt-text">
                  <span className="sr-ta-name">{creating ? 'Creating…' : <>Create “{q.trim()}”</>}</span>
                  <span className="sr-ta-sub">a new part of the product</span>
                </span>
              </button>
            )}

            {err && <p className="sr-ta-empty area-err">{err}</p>}
            {matches.length === 0 && !canCreate && (
              <p className="sr-ta-empty">No areas yet — type a name to create the first.</p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
